import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatMoney, formatMonth, formatNumber, formatPercent, hasPermission } from '@blimo/shared';
import { Badge, DataTable, ErrorState, PageHeader, SearchInput } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { useListState } from '../../lib/list-search.js';

const PORTAL_OPTIONS = [
  { value: '1', label: 'Im Portal freigeschaltet' },
  { value: '0', label: 'Nicht im Portal' },
];
const ARCHIVE_OPTIONS = [
  { value: '0', label: 'Nur aktive' },
  { value: '1', label: 'Nur archivierte' },
];

export function PropertiesListPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = { ...list.apiParams, filter: { q: search.q, portal_enabled: search.portal, archived: search.archived } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.properties(api, params));
  const { data: me } = useMe();
  // G-19 / F-125: Kennzahlen der aktuellen Seite (Backend: FinanceService, eine Anfrage je Seite) – nur mit finance.view
  const showFigures = hasPermission(me, 'finance.view');
  const pageIds = (data?.data ?? []).map((p) => p.id);
  const figuresQuery = useQuery({ ...adminQueries.financePropertyFigures(api, pageIds), enabled: showFigures && pageIds.length > 0 });
  const figures = useMemo(() => new Map((figuresQuery.data ?? []).map((f) => [f.property_id, f])), [figuresQuery.data]);
  const columns = useMemo(
    () => propertyColumns(showFigures ? { figures, loading: figuresQuery.isPending } : null),
    [showFigures, figures, figuresQuery.isPending],
  );

  return (
    <>
      <PageHeader title="Liegenschaften" description="Stammdaten aus WinCasa; Beschreibung und Vorbereitungsmodus werden hier gepflegt." />
      <FilterBar>
        <SearchInput
          label="Liegenschaften durchsuchen"
          placeholder="Name, Nummer, Straße, Ort …"
          value={search.q ?? ''}
          onValueChange={(v) => list.setFilter('q', v)}
        />
        <FilterSelect label="Kundenportal" value={search.portal} onChange={(v) => list.setFilter('portal', v)} options={PORTAL_OPTIONS} />
        <FilterSelect
          label="Archiv"
          value={search.archived}
          onChange={(v) => list.setFilter('archived', v)}
          options={ARCHIVE_OPTIONS}
          allLabel="Aktive und archivierte"
        />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Liegenschaften"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          sorting={list.sorting}
          onSortingChange={list.onSortingChange}
          emptyTitle="Keine Liegenschaften gefunden"
          emptyDescription={search.q || search.portal || search.archived ? 'Passen Sie Suche oder Filter an.' : undefined}
        />
      )}
    </>
  );
}

/**
 * @param {{ figures: Map<number, any>, loading: boolean }|null} finance  null = no finance.view → no figure columns
 */
function propertyColumns(finance) {
  const figure = (/** @type {number} */ id) => finance?.figures.get(id);
  const pending = finance?.loading ? '…' : '–';
  const financeColumns = finance
    ? [
        {
          id: 'open_items_balance',
          header: 'OP-Saldo',
          enableSorting: false,
          meta: { numeric: true },
          cell: ({ row }) => {
            const value = figure(row.original.id)?.open_items_balance;
            if (value == null) return pending;
            return <span className={value < 0 ? 'text-destructive' : undefined}>{value === 0 ? '–' : formatMoney(value)}</span>;
          },
        },
        {
          id: 'yield',
          header: 'Rendite',
          enableSorting: false,
          meta: { numeric: true },
          cell: ({ row }) => {
            const f = figure(row.original.id);
            if (!f) return pending;
            return f.yield == null ? '–' : <span title={f.yield_period ? `Stand ${formatMonth(f.yield_period)}` : undefined}>{formatPercent(f.yield)}</span>;
          },
        },
        {
          id: 'costs_last_6_months',
          header: 'Kosten (6 Mon.)',
          enableSorting: false,
          meta: { numeric: true },
          cell: ({ row }) => {
            const f = figure(row.original.id);
            if (!f) return pending;
            return f.costs_last_6_months == null ? '–' : formatMoney(f.costs_last_6_months);
          },
        },
      ]
    : [];
  return [
    { accessorKey: 'number', header: 'Nr.', cell: ({ row }) => <span className="numeric">{row.original.number ?? '–'}</span> },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Link to="/liegenschaften/$propertyId" params={{ propertyId: String(row.original.id) }} className="font-medium text-primary hover:underline">
            {row.original.name || `Liegenschaft #${row.original.id}`}
          </Link>
          {row.original.is_in_preparation && <Badge variant="warning">In Vorbereitung</Badge>}
          {row.original.archived_at && <Badge variant="neutral">Archiviert</Badge>}
        </div>
      ),
    },
    {
      id: 'street',
      header: 'Straße',
      enableSorting: false,
      cell: ({ row }) => [row.original.address?.street, row.original.address?.house_number].filter(Boolean).join(' ') || '–',
    },
    {
      id: 'postal_code',
      accessorFn: (r) => r.address?.postal_code,
      header: 'PLZ',
      cell: ({ row }) => <span className="numeric">{row.original.address?.postal_code ?? '–'}</span>,
    },
    { id: 'city', accessorFn: (r) => r.address?.city, header: 'Ort', cell: ({ row }) => row.original.address?.city ?? '–' },
    {
      accessorKey: 'units_count',
      header: 'Einheiten',
      meta: { numeric: true },
      cell: ({ row }) => formatNumber(row.original.units_count, { maximumFractionDigits: 0 }),
    },
    ...financeColumns,
    {
      accessorKey: 'portal_enabled',
      header: 'Portal',
      enableSorting: false,
      cell: ({ row }) => (row.original.portal_enabled ? <Badge variant="success">Freigeschaltet</Badge> : <Badge variant="neutral">Nein</Badge>),
    },
  ];
}
